import useSWR from "swr";
import { api, fmtEUR, fmtDate, apiUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FilePdf, Wrench } from "@phosphor-icons/react";

const fetcher = (url) => api.get(url).then((r) => r.data);

const DOC_TYPE_TITLE = {
    facture: "Facture",
    avoir: "Avoir",
    devis: "Devis",
    bon_de_commande: "Bon de commande",
    bon_livraison: "Bon de livraison",
    bon_intervention: "Bon d'intervention",
    devis_accepte: "Devis accepté",
    traite: "Traite",
    autre: "Document",
};

export default function InvoiceDetail({ invoiceId, open, onOpenChange }) {
    const { data: invoice } = useSWR(invoiceId ? `/invoices/${invoiceId}` : null, fetcher);
    const dtype = invoice?.document_type || "facture";
    const isIntervention = dtype === "bon_intervention";
    const heading = invoice?.invoice_number || DOC_TYPE_TITLE[dtype] || "Document";

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="invoice-detail">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl tracking-tight">
                        {heading}
                    </DialogTitle>
                    <DialogDescription className="font-mono text-xs">
                        {DOC_TYPE_TITLE[dtype]} · {invoice?.order_id}
                    </DialogDescription>
                </DialogHeader>

                {!invoice ? (
                    <div className="py-8 text-center text-muted-foreground">Chargement…</div>
                ) : (
                    <div className="space-y-5">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                            <Field label={isIntervention ? "Date intervention" : "Date commande"} value={fmtDate(invoice.order_date)} />
                            <Field label="Date document" value={fmtDate(invoice.invoice_date)} />
                            <Field label="Émetteur" value={invoice.seller || "—"} />
                            <Field label="Origine" value={invoice.marketplace || invoice.vendor || "—"} />
                        </div>

                        {isIntervention && invoice.intervention && (
                            <InterventionBlock data={invoice.intervention} />
                        )}

                        {!isIntervention && (
                            <div className="grid grid-cols-3 gap-4 p-4 bg-accent/40 rounded-md border border-border">
                                <div>
                                    <div className="overline">Montant HT</div>
                                    <div className="font-display text-lg font-bold tabular mt-1">
                                        {invoice.total_ht != null ? fmtEUR(invoice.total_ht, invoice.currency) : "—"}
                                    </div>
                                </div>
                                <div>
                                    <div className="overline">TVA</div>
                                    <div className="font-display text-lg font-bold tabular mt-1">
                                        {invoice.vat != null ? fmtEUR(invoice.vat, invoice.currency) : "—"}
                                    </div>
                                </div>
                                <div>
                                    <div className="overline">Total TTC</div>
                                    <div className="font-display text-lg font-bold tabular mt-1 text-primary">
                                        {fmtEUR(invoice.total_ttc, invoice.currency)}
                                    </div>
                                </div>
                            </div>
                        )}

                        {invoice.items?.length > 0 && (
                            <div>
                                <div className="overline mb-2">{isIntervention ? "Pièces / prestations" : "Articles"}</div>
                                <div className="border border-border rounded-md divide-y">
                                    {invoice.items.map((it, i) => (
                                        <div key={i} className="flex items-center justify-between p-3 text-sm">
                                            <div className="min-w-0">
                                                <div className="font-semibold truncate">{it.title}</div>
                                                <div className="text-xs text-muted-foreground">Qté {it.quantity}</div>
                                            </div>
                                            <div className="font-mono tabular text-sm">
                                                {it.unit_price != null ? fmtEUR(it.unit_price, invoice.currency) : "—"}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="flex items-center justify-between pt-2">
                            <Badge
                                variant="outline"
                                className={
                                    invoice.has_pdf
                                        ? "text-emerald-700 border-emerald-200"
                                        : "text-amber-700 border-amber-200"
                                }
                            >
                                {invoice.has_pdf ? "PDF disponible" : "PDF manquant"}
                            </Badge>
                            {invoice.has_pdf && (
                                <a
                                    href={apiUrl(`/invoices/${invoice.id}/pdf`)}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="invoice-pdf-link"
                                >
                                    <Button variant="outline">
                                        <FilePdf size={16} className="mr-2" />
                                        Ouvrir le PDF
                                    </Button>
                                </a>
                            )}
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, value }) {
    return (
        <div>
            <div className="overline">{label}</div>
            <div className="text-sm font-semibold mt-1">{value || "—"}</div>
        </div>
    );
}

function InterventionBlock({ data }) {
    const fullAddress = [data.customer_address, [data.customer_postcode, data.customer_city].filter(Boolean).join(" ")]
        .filter(Boolean).join(", ");
    return (
        <div className="rounded-md border border-violet-200 bg-violet-50/50 p-4 space-y-4" data-testid="intervention-block">
            <div className="flex items-center gap-2">
                <Wrench size={18} className="text-violet-700" />
                <h3 className="font-display font-bold text-violet-900">Ordre de réparation / Intervention</h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                <InterventionField label="Client" value={data.customer_name} />
                <InterventionField label="Téléphone" value={data.customer_phone} />
                <InterventionField label="Email" value={data.customer_email} />
                <InterventionField label="Adresse" value={fullAddress} />
                <InterventionField label="Appareil" value={data.device} />
                <InterventionField label="Technicien" value={data.technician} />
                {data.intervention_date && (
                    <InterventionField label="Date intervention" value={fmtDate(data.intervention_date)} />
                )}
            </div>

            {data.symptoms && (
                <div>
                    <div className="overline text-violet-800">Symptômes</div>
                    <div className="mt-1 text-sm text-violet-950 whitespace-pre-wrap bg-white/60 border border-violet-200 rounded p-2.5" data-testid="intervention-symptoms">
                        {data.symptoms}
                    </div>
                </div>
            )}

            {data.remarks && (
                <div>
                    <div className="overline text-violet-800">Remarques / Diagnostic</div>
                    <div className="mt-1 text-sm text-violet-950 whitespace-pre-wrap bg-white/60 border border-violet-200 rounded p-2.5" data-testid="intervention-remarks">
                        {data.remarks}
                    </div>
                </div>
            )}
        </div>
    );
}

function InterventionField({ label, value }) {
    if (!value) return null;
    return (
        <div>
            <div className="overline text-violet-800">{label}</div>
            <div className="text-sm font-semibold text-violet-950 mt-0.5">{value}</div>
        </div>
    );
}
